import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CheckCheck, ChevronRight, Clock, Store } from "lucide-react";
import { TruncatedText, focusRingInsetClass } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { HandshakeSessionListItem } from "@/types";
import { SessionStatusBadge, ChannelBadge } from "./HandshakeBadges";
import { HandshakeBookingDetails } from "./HandshakeBookingDetails";
import { SESSION_STATUS_CONFIG, formatRelativeDate } from "./config";

interface HandshakeListItemProps {
  session: HandshakeSessionListItem;
  to: string;
  className?: string;
}

/** One conversation row — render inside a divided list container. */
export function HandshakeListItem({
  session,
  to,
  className,
}: HandshakeListItemProps) {
  const { t } = useTranslation("handshake");
  const statusConfig = SESSION_STATUS_CONFIG[session.status];
  const StatusIcon = statusConfig?.icon;

  return (
    <li className={className}>
      <Link
        to={to}
        className={cn(
          "group flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-surface-50 focus-visible:bg-surface-50 sm:gap-4 dark:hover:bg-surface-100/50",
          focusRingInsetClass,
        )}
      >
        <span
          className={cn(
            "hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg sm:flex",
            statusConfig?.bgColor,
          )}
          aria-hidden
        >
          {StatusIcon ? (
            <StatusIcon className={cn("h-4 w-4", statusConfig?.color)} />
          ) : null}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <TruncatedText
              as="h3"
              className="min-w-0 text-sm font-semibold text-surface-900"
            >
              {session.title}
            </TruncatedText>
            <span className="flex shrink-0 items-center gap-1.5">
              <SessionStatusBadge status={session.status} size="sm" />
              <ChannelBadge channel={session.customer_channel} size="sm" />
            </span>
          </div>
          <HandshakeBookingDetails
            context={session.context}
            variant="inline"
            className="mt-1 text-[13px]"
          />
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-surface-500">
            <span className="inline-flex items-center gap-1.5">
              <Store className="h-3.5 w-3.5 text-surface-400" aria-hidden />
              {session.seller_name}
            </span>
            <span className="inline-flex items-center gap-1.5 tabular-nums">
              <Clock className="h-3.5 w-3.5 text-surface-400" aria-hidden />
              {formatRelativeDate(session.created_at)}
            </span>
            {session.status === "CONFIRMED" ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-success-700">
                <CheckCheck className="h-3.5 w-3.5" aria-hidden />
                {t("listItem.agreementReached")}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronRight
          className="hidden h-4 w-4 shrink-0 text-surface-300 transition-transform group-hover:translate-x-0.5 group-hover:text-surface-500 sm:block"
          aria-hidden
        />
      </Link>
    </li>
  );
}
